import type { RequestMiddleware } from 'graphql-request';
import { APIBaseURL } from '@meterup/common';
import { GraphQLClient } from 'graphql-request';

import { appendDebugQueryParams } from './utils';

export { GraphQLClient };

/**
 * When developing on a local machine and pointing the proxy at prod, we want to operate in read-only mode
 * so that there is no risk of unreviewed code making any modifications to the production data.
 *
 * Note that in prod we're serving static files - there is no vite server therefore the whole section below is moot,
 * and the middleware becoms a passthrough for requests.
 */
const blockMutationsIfProxyingToProd: RequestMiddleware = (request) => {
  if (import.meta.env.VITE_PROXY === 'prod') {
    const { body } = request;
    const requestIsMutation = typeof body === 'string' && body.includes('mutation');
    if (requestIsMutation) {
      throw new Error(
        'Mutations are disallowed when proxying into production. You are in read-only mode.',
      );
    }
  }

  return request;
};

/**
 * Append debug query parameters in development
 *
 * @see {@link appendDebugQueryParams}
 */
const addDebugQueryParams: RequestMiddleware = (request) => ({
  ...request,
  url: appendDebugQueryParams(request.url, {
    operationName: request.operationName,
    client: 'TanStack',
  }),
});

const requestMiddleware: RequestMiddleware = async (request) =>
  addDebugQueryParams(await blockMutationsIfProxyingToProd(request));

export const GRAPHQL_BASE_PATH = 'v2/graphql';
export const defaultGraphQLURL = `${APIBaseURL}/${GRAPHQL_BASE_PATH}`;

export const defaultGraphQLRequestConfig = {
  credentials: 'include',
  // NOTE: To get the error AND data fields using this option the `rawRequest` method MUST be used.
  //        Currently, we use the `request` method, so this will not have an effect. We will need to
  //        figure out how to use `rawRequest` with react-query to return both the error and data fields.
  errorPolicy: 'all',
  requestMiddleware,
} as const;

export const defaultGraphQLClient = new GraphQLClient(
  defaultGraphQLURL,
  defaultGraphQLRequestConfig,
);
